/*
 * Copyright (c) 2024. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Stack, Typography } from '@mui/material';
import type { EnvInfo } from '@poweralpha/common';
import { upperCase } from 'lodash';
import { useMemo } from 'react';
import { SalesforceIcon } from './react-components/salesforce/SalesforceLinkIcon';

const PROD_ENVS = ['demo', 'prod'];

interface Props {
  envInfo: EnvInfo | undefined;
}

function EnvLabel({ envInfo }: Readonly<Props>) {
  const envLabel = useMemo(
    () =>
      envInfo?.env && (!PROD_ENVS.includes(envInfo.env) || process.env.NODE_ENV === 'development')
        ? upperCase(envInfo.env)
        : null,
    [envInfo],
  );

  if (!envLabel) {
    return null;
  }

  const sfUser = envInfo?.sfUsername?.split('@')[0];
  const sfDomain = envInfo?.sfUsername?.split('@')[1].split('.').at(-1);

  return (
    <Stack>
      <Typography
        sx={{
          color: 'error.main',
          fontWeight: 'fontWeightBold',
          ml: 2,
          fontSize: { sm: 16, xs: 8 },
        }}
      >
        {envLabel}
        <i>{process.env.NODE_ENV === 'development' ? ' (local)' : ''}</i>
      </Typography>
      <Stack
        direction="row"
        spacing={0.5}
        sx={{
          ml: 2,
        }}
      >
        <SalesforceIcon height={16} />
        <Typography
          sx={{
            color: 'info.main',
            fontSize: { sm: 12, xs: 8 },
          }}
        >
          {sfUser === 'administration_test' ? '' : sfUser}
          <i>{sfDomain === 'com' ? '' : `(${sfDomain})`}</i>
        </Typography>
        {sfDomain === 'com' && <WarningAmberIcon color="warning" fontSize="small" />}
      </Stack>
    </Stack>
  );
}

export default EnvLabel;
